/**
 * SparkMoment — the six-beat creation animation.
 *
 * Beats, approximate timings:
 *   0.00–0.30s  button compresses + glows
 *   0.30–0.60s  stamp overlay — manifest bytes scrolling through
 *   0.60–1.20s  particle burst; "SPARK" word rises
 *   1.20–1.80s  GI card materializes (scale + clip-path reveal)
 *   1.80–2.10s  hashes fade in beneath the card
 *   2.10–2.40s  download button slides into view
 *
 * Respects `prefers-reduced-motion`: reduced users see the final
 * static success state immediately, skipping every transitional
 * phase. Nothing about the semantics of the moment changes.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';

import { Button } from '@/components/Button';
import { DomainBadge } from '@/components/Badge';
import { DomainIcon } from '@/components/DomainIcon';
import { HashDisplay } from '@/components/HashDisplay';
import type { GiBundle, NormalizedDomain } from '@/api/types';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { normalizeDomain, truncateHash } from '@/utils/format';

export type SparkStage =
  | 'idle'        // before click
  | 'signing'     // beats 1-3 — work is happening
  | 'revealing'   // beats 4-5 — result showing
  | 'done';       // beat 6 — download available

export function SparkMoment({
  stage,
  bundle,
  onDownload,
  onBackToVault,
  onRetry,
}: {
  stage: SparkStage;
  bundle: GiBundle | null;
  onDownload: () => void;
  onBackToVault: () => void;
  onRetry: () => void;
}) {
  const reduced = useReducedMotion();
  const { t } = useTranslation('spark');
  const [revealed, setRevealed] = useState(false);

  // Advance the inner reveal trigger once the sign completes.
  useEffect(() => {
    if (stage === 'revealing' || stage === 'done') {
      if (reduced) {
        setRevealed(true);
      } else {
        const id = window.setTimeout(() => setRevealed(true), 300);
        return () => window.clearTimeout(id);
      }
    } else {
      setRevealed(false);
    }
    return undefined;
  }, [stage, reduced]);

  const normalized: NormalizedDomain | null = useMemo(
    () => (bundle ? normalizeDomain(bundle.manifest.domain) : null),
    [bundle],
  );

  const particleTransition = reduced
    ? { duration: 0 }
    : { duration: 0.9, ease: 'easeOut' as const };

  return (
    <div
      role="region"
      aria-live="polite"
      className="relative flex min-h-[420px] flex-col items-center justify-center gap-6 overflow-hidden rounded-xl border border-border bg-surface p-8"
    >
      {/* Beats 1-3 — stamp + burst overlay. Only visible while signing. */}
      <AnimatePresence>
        {stage === 'signing' && !reduced ? (
          <motion.div
            key="burst"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.4 }}
            transition={particleTransition}
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <div className="relative flex items-center justify-center">
              {/* Word "SPARK" rising in */}
              <motion.span
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="font-mono text-5xl font-bold tracking-[0.2em] text-spark"
              >
                SPARK
              </motion.span>

              {/* 18 radial particles */}
              {Array.from({ length: 18 }).map((_, i) => {
                const angle = (i / 18) * Math.PI * 2;
                const dx = Math.cos(angle) * 160;
                const dy = Math.sin(angle) * 160;
                return (
                  <motion.span
                    key={i}
                    initial={{ x: 0, y: 0, opacity: 0 }}
                    animate={{ x: dx, y: dy, opacity: [0, 1, 0] }}
                    transition={{
                      duration: 0.9,
                      delay: 0.15 + (i % 3) * 0.05,
                      ease: 'easeOut',
                    }}
                    className="absolute h-1.5 w-1.5 rounded-full bg-spark"
                  />
                );
              })}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Beats 4-6 — result card. */}
      <AnimatePresence>
        {revealed && bundle && normalized ? (
          <motion.div
            key="card"
            initial={{ opacity: 0, scale: 0.9, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: reduced ? 0 : 0.55, ease: 'easeOut' }}
            className="flex w-full max-w-md flex-col items-center gap-4 text-center"
          >
            <span
              className={`flex h-12 w-12 items-center justify-center rounded-lg bg-domain-${normalized}-soft text-domain-${normalized}`}
            >
              <DomainIcon domain={normalized} className="h-6 w-6" />
            </span>
            <DomainBadge
              domain={normalized}
              label={
                typeof bundle.manifest.domain === 'string'
                  ? bundle.manifest.domain
                  : bundle.manifest.domain.Custom
              }
            />
            <h2 className="text-xl font-semibold text-ink">{bundle.manifest.name}</h2>
            <p className="text-sm text-ink-muted">{bundle.manifest.description}</p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: reduced ? 0 : 0.35, delay: reduced ? 0 : 0.15 }}
              className="mt-1 flex flex-col items-center gap-1 text-[11px] text-ink-muted"
            >
              <span>
                root_id:{' '}
                <span className="font-mono text-ink">
                  {truncateHash(bundle.manifest.root_id, 8, 6)}
                </span>
              </span>
              <HashDisplay value={bundle.signed_entity.manifest_hash} />
              <HashDisplay value={bundle.signed_entity.signer_public_key} />
            </motion.div>

            <AnimatePresence>
              {stage === 'done' ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: reduced ? 0 : 0.3, delay: reduced ? 0 : 0.3 }}
                  className="mt-4 flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-center"
                >
                  {/* Verifier QR — encodes the public `/v/:id` route on
                      whatever origin the user is currently on (production
                      → app.growing-intelligence.com; dev → localhost,
                      which still scans on the same machine). Sits next
                      to the action buttons, not in place of them. */}
                  <div className="flex flex-col items-center gap-1.5">
                    <div className="rounded-lg border border-border bg-white p-2">
                      <QRCodeSVG
                        value={`${window.location.origin}/v/${bundle.manifest.root_id}`}
                        size={200}
                        level="M"
                        marginSize={0}
                      />
                    </div>
                    <p className="text-[11px] text-ink-muted" dir="auto">
                      {t('moment.scan_to_verify')}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <Button variant="primary" onClick={onDownload} leading={<DownloadGlyph />}>
                      {t('moment.download')}
                    </Button>
                    <Button variant="secondary" onClick={onBackToVault}>
                      {t('moment.back_to_vault')}
                    </Button>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Error / idle fallback copy. Shown only when nothing else is. */}
      {stage === 'idle' ? (
        <div className="text-center text-sm text-ink-muted">
          {t('moment.idle_hint')}
          <div className="mt-4">
            <Button variant="ghost" size="sm" onClick={onRetry}>
              {t('moment.retry')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function DownloadGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 4v12M6 12l6 6 6-6M4 20h16" />
    </svg>
  );
}
